/**
 * openAttachment — safely open an attachment stored as a base64 data URL.
 *
 * Why this exists (iter-212 — Feb 2026):
 *   Chrome (and most modern browsers) BLOCK direct navigation to `data:` URLs
 *   larger than ~2MB for security reasons. Our task/action attachments are
 *   stored as `{ data_url: "data:image/png;base64,..." }` inside the task
 *   document, so `<a href={a.data_url}>` silently fails to open — this is
 *   what our production users experience as "المرفقات ما تفتح".
 *
 * The workaround:
 *   Decode the base64 → build a Blob → create a browser-managed ObjectURL →
 *   open THAT URL in a new tab. Blob URLs have no size limit and pass the
 *   browser's security checks.
 *
 * Fallback:
 *   If the attachment already has a `file_id` (uploaded via /api/upload/file),
 *   just open the backend URL directly.
 */
export function openAttachment(att, { token } = {}) {
  if (!att) return;

  // Preferred path — attachment stored on the server via /api/upload/file.
  if (att.file_id) {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const url = `${base}/api/upload/file/${att.file_id}?token=${encodeURIComponent(token || '')}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }

  // Legacy path — attachment embedded as a base64 data URL.
  const dataUrl = att.data_url || att.dataUrl;
  if (!dataUrl || typeof dataUrl !== 'string') return;

  // Small enough to just open directly (< 1.8MB base64 = ~1.35MB binary).
  if (dataUrl.length < 1_800_000) {
    window.open(dataUrl, '_blank', 'noopener,noreferrer');
    return;
  }

  // Large payload — go through Blob URL to bypass Chrome's data-URL limit.
  try {
    const commaIdx = dataUrl.indexOf(',');
    if (commaIdx === -1) throw new Error('bad data url');
    const header = dataUrl.slice(0, commaIdx);
    const base64 = dataUrl.slice(commaIdx + 1);
    // header looks like: "data:image/png;base64"
    const mime = (header.match(/data:([^;]+)/)?.[1]) || 'application/octet-stream';

    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);

    const blob = new Blob([bytes], { type: mime });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank', 'noopener,noreferrer');
    // Release the object URL after a few minutes to free memory.
    setTimeout(() => URL.revokeObjectURL(url), 5 * 60 * 1000);
    if (!win) {
      // Popup blocked — offer download instead so the user gets the file.
      const a = document.createElement('a');
      a.href = url;
      a.download = att.name || att.label || 'attachment';
      a.click();
    }
  } catch (e) {
    // Last resort — try the raw data URL. Might fail on Chrome for big files
    // but nothing else we can do at this point.
    window.open(dataUrl, '_blank', 'noopener,noreferrer');
  }
}
